import {Component, DestroyRef, inject, OnInit, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ConfirmationService} from 'primeng/api';
import {AdminsService} from '../../services/admins.service';
import {AdminUserModel} from '../../../../api/model/Admin-user.model';
import {Page, PageQuery, SortQuery} from '../../../../api/model/shared/Page.model';
import {environment} from '../../../../../environments/environment';
import {ActivatedRoute, Router} from '@angular/router';
import {AdminSearchService} from '../../services/admin-search.service';
import {ApiService} from '../../../../api/service/api.service';
import {TjKeycloakService} from '../../../../core/services';

@Component({
  selector: 'tj-admins',
  templateUrl: './admins.component.html',
  styleUrls: ['./admins.component.scss'],
  providers: [AdminSearchService, AdminsService]
})
export class AdminsComponent implements OnInit {
  protected readonly env = environment;

  page: Page<AdminUserModel> = {
    totalElements: 0,
    page: 0,
    size: this.env.rowsPerPage[0],
    response: [],
  };

  private readonly api = inject(ApiService);
  private readonly adminSearchService = inject(AdminSearchService);
  private readonly adminsService = inject(AdminsService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly keycloakService = inject(TjKeycloakService);

  admins = signal<AdminUserModel[]>([]);

  protected pageQuery: PageQuery = {
    size: this.env.rowsPerPage[0],
    number: 0,
  };

  protected sortQuery: SortQuery = {
    property: 'firstName',
    direction: 'asc',
  };

  searchFilter;
  searchQuery;

  showEditDialog = false;
  editingAdmin: AdminUserModel | null = null;

  constructor() {
  }

  ngOnInit() {
    this.adminSearchService.listenQueryParams();
    this.adminSearchService.searchData.subscribe(({filters, searchQuery}) => {
      this.searchFilter = filters;
      this.searchQuery = searchQuery;
    });

    this.route.queryParams.subscribe(params => {
      this.pageQuery.number = params.page || 0;
      this.pageQuery.size = +params.size || this.pageQuery.size;
      this.sortQuery.property = params.property || 'firstName';
      this.sortQuery.direction = params.direction || 'asc';
      this.loadInitialData();
    });
  }

  private loadInitialData(): void {
    this.api.admin.findList(this.pageQuery, this.sortQuery, this.searchQuery).subscribe({
      next: (adminsPage: Page<AdminUserModel>) => {
        this.page = adminsPage;
        this.admins.set(adminsPage.response);
      }
    });
  }

  sort(event): void {
    this.sortQuery = {
      property: event.field,
      direction: event.order === -1 ? 'desc' : 'asc',
    };
    this.router.navigate([], {
      queryParams: this.sortQuery,
      queryParamsHandling: 'merge'
    }).then();
  }

  onGoToPage(event): void {
    this.router.navigate([], {
      queryParams: {page: event.page, size: event.rows},
      queryParamsHandling: 'merge',
    }).then();
  }

  onCreateNewAdmin(): void {
    this.editingAdmin = null;
    this.showEditDialog = true;
  }

  onEditAdmin(admin: AdminUserModel): void {
    this.editingAdmin = admin;
    this.showEditDialog = true;
  }

  onDeleteAdmin(admin: AdminUserModel): void {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete admin "${admin.firstName} ${admin.lastName}"? This action cannot be undone.`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.deleteAdmin(admin);
      }
    });
  }

  closeEditDialog(): void {
    this.showEditDialog = false;
    this.editingAdmin = null;
  }

  onSaveUser(adminData: Partial<AdminUserModel>): void {
    if (!this.keycloakService.hasRole('ADMIN_EDIT')) {
      return;
    }
    const admin = this.editingAdmin;
    if (admin) {
      this.adminsService.updateAdmin(admin.id, adminData).pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe({
        next: () => {
          this.closeEditDialog();
          this.loadInitialData();
        }
      });
    } else {
      this.adminsService.createAdmin(adminData).pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe({
        next: () => {
          this.closeEditDialog();
          this.loadInitialData();
        }
      });
    }
  }

  private deleteAdmin(admin: AdminUserModel): void {
    this.adminsService.deleteAdmin(admin.id).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: () => {
        this.loadInitialData();
      }
    });
  }
}
